import { Component, inject, signal } from '@angular/core';
import { formatDate } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastService } from '../../../ui';
import { minDateValidator } from '../../../shared/validators/min-date.validator';
import { maxIsoDate, todayIso } from '../../../shared/utils/iso-date.util';
import { ContractsService } from '../contracts.service';
import { AuthService } from '../../../core/auth/auth.service';
import {
  HLM_DIALOG_SHELL,
  HLM_FORM_FIELD,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSpinnerComponent,
} from '../../../ui';

export interface EndContractDialogData {
  contractId: number;
  invoiceDay: number;
  currentPlan: string;
  startDate: string;
  /** Optional overrides — callers can customize wording per context. */
  title?: string;
  confirmLabel?: string;
  /** Optional pre-filled end date (yyyy-MM-dd). Leaves the field empty if omitted. */
  defaultEndDate?: string;
}

@Component({
  selector: 'cmt-end-contract-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL, ...HLM_FORM_FIELD,
    HlmBtnDirective, HlmIconComponent, HlmInputDirective, HlmSpinnerComponent,
  ],
  template: `
    <hlm-dialog-surface size="sm">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="destructive">
          <hlm-icon name="lucideCircleX" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>{{ data.title ?? 'End Contract' }}</h2>
          <p hlmDialogDescription>
            Contract
            <span class="font-mono-feat">{{ data.contractId }}</span> ·
            <strong>{{ data.currentPlan }}</strong> ·
            started <span class="font-mono-feat">{{ data.startDate }}</span>
          </p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody>
        <form [formGroup]="form" class="flex flex-col gap-4">
          <hlm-form-field>
            <label hlmLabel for="endDate">End date</label>
            <input id="endDate" hlmInput type="date" formControlName="endDate"
                   [min]="minDateStr" />
            @if (form.controls.endDate.hasError('minDate')) {
              <p hlmFieldError>
                @if (minDateReason === 'contract-start') {
                  The end date cannot be before the contract starts ({{ minDateDisplay }}).
                } @else {
                  The end date cannot be in the past (earliest {{ minDateDisplay }}).
                }
              </p>
            }
          </hlm-form-field>
        </form>
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close()" [disabled]="submitting()">
          Cancel
        </button>
        <button hlmBtn variant="destructive" (click)="submit()" [disabled]="!canSubmit()">
          @if (submitting()) {
            <hlm-spinner size="16" /> Ending...
          } @else {
            <hlm-icon name="lucideCircleX" size="0.9rem" />
            {{ data.confirmLabel ?? 'End Contract' }}
          }
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class EndContractDialogComponent {
  readonly data = inject<EndContractDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<EndContractDialogComponent>);
  private readonly contractsService = inject(ContractsService);
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly fb = inject(FormBuilder);

  readonly submitting = signal(false);
  /**
   * Old CMT states the rule in its own error message ("End date can not be in the past or
   * before the contract starts", `endContractForm.html:35`) but never enforces it: the
   * template binds `min-date="minDate"` while `$scope.minDate` is only ever assigned behind
   * `Date.parse($scope.contract.startDate) > Date.parse($scope.minDate)`
   * (`contractRevisions.js:1084`), which compares against `NaN` and therefore never runs —
   * so the picker gets `undefined` and imposes nothing. AMIE implements the stated rule
   * (decision 2026-09-04): the floor is the later of today and the contract start.
   */
  readonly minDateStr = maxIsoDate(todayIso(), this.data.startDate);
  readonly minDateDisplay = formatDate(this.minDateStr, 'dd.MM.yyyy', 'en');
  /** Which half of the rule the floor came from — the message names the actual reason. */
  readonly minDateReason = this.minDateStr === this.data.startDate ? 'contract-start' : 'today';

  readonly form: FormGroup = this.fb.group({
    // `[min]` on a date input is presentational only - the real check lives here.
    endDate: [
      this.data.defaultEndDate ?? null,
      [Validators.required, minDateValidator(() => this.minDateStr)],
    ],
  });

  canSubmit(): boolean {
    return this.form.valid && !this.submitting();
  }

  submit(): void {
    if (!this.canSubmit()) return;
    this.submitting.set(true);

    const endDate = this.form.value.endDate;

    this.authService.user$.subscribe((user) => {
      this.contractsService.modifyContract(
        this.data.contractId,
        [{ type: 'REPLACE', attribute: 'endDate', value: endDate }],
        { employeeId: String(user?.userId ?? 0) },
      ).subscribe({
        next: () => {
          this.toast.open('Contract ended successfully', 'OK', { duration: 4000 });
          this.dialogRef.close(true);
        },
        error: () => {
          this.toast.open('Failed to end contract', 'OK', { duration: 6000 });
          this.submitting.set(false);
        },
      });
    }).unsubscribe();
  }
}
